import { createElement, Fragment, type ReactNode } from 'react';

/**
 * What the coach wrote, as the family reads it.
 *
 * THE NOTE IS HTML THAT NOBODY ON THE SERVER HAS CHECKED. The coach's editor
 * filters on the way out, and the API stores what it is given. So this screen
 * does not trust it: the HTML is parsed with DOMParser — which runs no script
 * and fetches no image — and rebuilt as React elements from an allowlist. It
 * is never injected. Anything outside the list is reduced to its text, and
 * no attribute survives, because no attribute is ever read.
 *
 * The list is the editor's four tools and the paragraphs they sit in: bold,
 * italic, bullet list, numbered list.
 */

const ALLOWED: Record<string, string> = {
  B: 'b',
  STRONG: 'strong',
  I: 'i',
  EM: 'em',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  P: 'p',
  DIV: 'p',
};

/** Their contents are code or styling, never words the coach meant to show. */
const DROPPED = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT']);

function rebuild(node: Node, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  if (DROPPED.has(element.tagName)) return null;
  if (element.tagName === 'BR') return <br key={key} />;

  const children = [...element.childNodes].map(rebuild);
  const tag = ALLOWED[element.tagName];
  return tag
    ? createElement(tag, { key }, ...children)
    : createElement(Fragment, { key }, ...children);
}

export function NoteRead({ html }: { html: string }) {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return <div className="note-read rich">{[...body.childNodes].map(rebuild)}</div>;
}
